import { useCallback, useEffect, useState } from 'react';
import { NativeBridge } from '../services/NativeBridge';

const SEARCH_TARGET_SELECTOR = '[data-quantis-search-target="true"]';

function isAvailableSearchTarget(element: Element | null): element is HTMLElement {
  if (!(element instanceof HTMLElement)) return false;
  if (!element.isConnected) return false;
  if (element.hasAttribute('disabled')) return false;
  if (element.hasAttribute('hidden')) return false;
  if (element.hasAttribute('inert')) return false;
  if (element.getAttribute('aria-hidden') === 'true') return false;
  if (element.closest('[hidden], [aria-hidden="true"], [inert]')) {
    return false;
  }
  return true;
}

function findAvailableSearchTarget(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const targets = Array.from(document.querySelectorAll(SEARCH_TARGET_SELECTOR));
  return targets.find(isAvailableSearchTarget) ?? null;
}

export function activateContextualSearchTarget(): boolean {
  const target = findAvailableSearchTarget();
  if (!target) return false;

  target.focus({ preventScroll: true });

  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    target.select();
  }

  return true;
}

function observeContextualSearchAvailability(onChange: (available: boolean) => void): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) {
    onChange(false);
    return () => {};
  }

  let timerId: ReturnType<typeof setTimeout> | null = null;

  const sync = () => {
    timerId = null;
    onChange(Boolean(findAvailableSearchTarget()));
  };

  // Debounce at 300ms so DOM-mutation bursts from React renders coalesce.
  const scheduleSync = () => {
    if (timerId !== null) return;
    timerId = setTimeout(sync, 300);
  };

  // Watch structural DOM changes and ARIA/state attributes only.
  // Omitting 'class' and 'style' prevents firing on every React render
  // (class/style churn is the main source of forced-reflow violations here).
  const observer = new MutationObserver(scheduleSync);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'data-state', 'hidden', 'open'],
  });

  window.addEventListener('resize', scheduleSync);
  window.addEventListener('orientationchange', scheduleSync);
  // 'focusin' is intentionally omitted: focus changes don't affect whether
  // a search target exists, and it fired on every click, scheduling a
  // getBoundingClientRect call during an already-dirty React render.

  sync();

  return () => {
    observer.disconnect();
    window.removeEventListener('resize', scheduleSync);
    window.removeEventListener('orientationchange', scheduleSync);
    if (timerId !== null) {
      clearTimeout(timerId);
    }
  };
}

export function useContextualSearch(enabled = true) {
  const computeAvailability = useCallback(() => (
    enabled &&
    (
      NativeBridge.hasRegisteredContextualSearchProvider() ||
      Boolean(findAvailableSearchTarget())
    )
  ), [enabled]);

  const [isAvailable, setIsAvailable] = useState<boolean>(() => computeAvailability());

  useEffect(() => {
    if (!enabled) {
      setIsAvailable(false);
      return () => {};
    }

    const refreshAvailability = () => {
      setIsAvailable(computeAvailability());
    };

    const stopObservingDom = observeContextualSearchAvailability(() => {
      refreshAvailability();
    });
    const unsubscribe = NativeBridge.on('contextualSearchAvailabilityChanged', refreshAvailability);

    refreshAvailability();

    return () => {
      stopObservingDom();
      unsubscribe();
    };
  }, [computeAvailability, enabled]);

  const activate = useCallback(() => {
    if (!enabled) return;

    if (NativeBridge.activateRegisteredContextualSearch({ preferNativeField: NativeBridge.isRunningNatively() })) {
      return;
    }
    activateContextualSearchTarget();
  }, [enabled]);

  return { isAvailable, activate };
}
